import { STAGED_WORKBENCH_SOURCE } from '../history/sessionNavigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@hooks/useConfirm';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';
import { drbfmService } from '../../../services/drbfm.service';
import type {
  DRBFMWorkflowResponse,
  MatrixCellUpdateRequest,
  UpdateCountermeasureReviewRequest,
  UpdateDesignChangeReviewRequest,
  UpdateFailureModeReviewRequest,
} from '../../../types/drbfm';
import type { WorkflowStartValue } from './DRBFMWorkflowStartStep';
import { submitExtractionArtifacts } from './stagedWorkflowArtifacts';
import { stagedWorkflowStartSchema } from '@/features/drbfm/schemas/stagedWorkflowStart.schema';
import {
  clampSessionTitle,
  countReviewedDesignChanges,
  countReviewedFailureModes,
  initialStartValue,
} from './useStagedWorkflow.helpers';

interface UseStagedWorkflowOptions {
  resumeSessionId?: string | null;
  onSession?: (sessionId: string) => void;
  /** Lets the page ask before a mode switch throws this run away. */
  onContentChange?: (hasContent: boolean) => void;
}

export const useStagedWorkflow = ({ onContentChange, resumeSessionId, onSession }: UseStagedWorkflowOptions = {}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [startValue, setStartValue] = useState<WorkflowStartValue>(initialStartValue);
  const [itemName, setItemName] = useState('電動リッド脚部');
  const [workflow, setWorkflow] = useState<DRBFMWorkflowResponse | null>(null);
  const [oldText, setOldText] = useState('');
  const [newText, setNewText] = useState('');
  const [changeReportText, setChangeReportText] = useState('');
  const [oldCadModelId, setOldCadModelId] = useState('');
  const [newCadModelId, setNewCadModelId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!resumeSessionId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    drbfmService.getWorkflow(resumeSessionId).then(saved => {
      if (cancelled) return;
      setWorkflow(saved);
      setItemName(saved.session.itemName);
      setStartValue({
        workflowPhase: saved.session.workflowPhase ?? initialStartValue.workflowPhase,
        inputPattern: saved.session.inputPattern ?? initialStartValue.inputPattern,
        changeGranularity: saved.session.changeGranularity ?? initialStartValue.changeGranularity,
      });
      // API returns artifacts in creation order; only the latest active input
      // of each type belongs in the editor when resuming/reanalysing.
      const latest = (types: string[]) => [...saved.artifacts].reverse().find(
        a => (!a.status || a.status === 'active') && types.includes(a.artifactType),
      );
      const text = (types: string[]) => {
        const value = latest(types)?.payload.text;
        return typeof value === 'string' ? value : '';
      };
      setChangeReportText(text(['change_report']));
      setOldText(text(['old_spec', 'old_drawing']));
      setNewText(text(['new_spec', 'new_drawing']));
      setOldCadModelId(latest(['old_cad'])?.cadModelId ?? '');
      setNewCadModelId(latest(['new_cad'])?.cadModelId ?? '');
    }).catch(err => {
      if (!cancelled) setError(resolveApiErrorMessage(t, err, {
        fallbackKey: 'drbfm.workflow.logic.stageRunFailed',
      }));
    }).finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  // Translation changes must not reload and overwrite edits in progress.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeSessionId]);

  // Report whether this run holds unsaved work, so the page can confirm before
  // a mode switch discards it (restored from our pre-merge version).
  const hasContent = Boolean(workflow || oldText || newText || changeReportText);
  useEffect(() => {
    onContentChange?.(hasContent);
  }, [hasContent, onContentChange]);

  const sessionId = workflow?.session?.id;

  const runSafely = async (fn: () => Promise<void>): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setError(resolveApiErrorMessage(t, err, {
        fallbackKey: 'drbfm.workflow.logic.stageRunFailed',
      }));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // A re-run supersedes the rows the stage produced last time, including any the
  // reviewer has not triaged yet.
  const confirmRegenerate = async (existingRows: number) => existingRows === 0 || confirm({
    title: t('drbfm.confirm.regenerateStage.title'),
    description: t('drbfm.confirm.regenerateStage.description'),
    confirmLabel: t('drbfm.confirm.regenerateStage.confirm'),
  });

  const handleStart = async () => {
    // No RHF: itemName is hook state shared with the page, so the schema supplies rules only.
    const parsed = stagedWorkflowStartSchema.safeParse({ itemName });
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    await runSafely(async () => {
      const created = await drbfmService.createSession({
        itemName,
        title: clampSessionTitle(
          t('drbfm.workflow.logic.sessionTitle', '{{itemName}} 工程別DRBFM', { itemName }),
        ),
      });
      const configured = await drbfmService.configureWorkflow(created.id, {
        ...startValue,
        workflowMetadata: { source: STAGED_WORKBENCH_SOURCE },
      });
      setWorkflow(configured);
      onSession?.(created.id);
    });
  };

  const handleExtractChanges = async () => {
    if (!sessionId) return;
    if (!(await confirmRegenerate(workflow?.designChanges.length ?? 0))) return;
    await runSafely(async () => {
      try {
        await submitExtractionArtifacts(sessionId, startValue.inputPattern, {
          changeReportText,
          oldText,
          newText,
          oldCadModelId,
          newCadModelId,
        }, t);
      } catch (err) {
        // Its input-validation copy is already localized and tells the reader
        // which field to fix, so the resolver must not flatten it. The API
        // rejections it can also raise are plain ApiError objects, not Errors,
        // and fall through to runSafely.
        if (!(err instanceof Error)) throw err;
        setError(err.message);
        return;
      }
      const result = await drbfmService.runWorkflowStage(sessionId, 'extract_changes', {
        options: {
          inputPattern: startValue.inputPattern,
          changeGranularity: startValue.changeGranularity,
        },
      });
      setWorkflow(result.workflow);
      // Reported directly rather than thrown: this copy is already localized,
      // and the stage's own errorMessage is English server prose.
      if (result.stageRun.status !== 'completed') {
        setError(t('drbfm.workflow.logic.extractChangesFailed', '変更点抽出に失敗しました'));
        return;
      }
      const normalized = await drbfmService.runWorkflowStage(
        sessionId,
        'normalize_changes',
      );
      setWorkflow(normalized.workflow);
      if (normalized.stageRun.status !== 'completed') {
        setError(t('drbfm.workflow.logic.normalizeChangesFailed', '変更点の正規化に失敗しました'));
      }
    });
  };

  const handleReviewChange = async (
    designChangeId: string,
    request: UpdateDesignChangeReviewRequest,
  ) => {
    if (!sessionId) return false;
    return runSafely(async () => {
      const updated = await drbfmService.updateDesignChangeReview(
        sessionId,
        designChangeId,
        request,
      );
      setWorkflow((current) => current ? {
        ...current,
        designChanges: current.designChanges.map((change) => (
          change.id === designChangeId ? { ...change, ...updated } : change
        )),
      } : current);
      const readiness = await drbfmService.getReadiness(sessionId);
      setWorkflow((current) => current ? { ...current, readiness } : current);
    });
  };

  const handleConfirmComparability = async (designChangeId: string, rationale: string) => {
    if (!sessionId) return;
    await runSafely(async () => {
      const updated = await drbfmService.confirmComparability(sessionId, designChangeId, rationale);
      setWorkflow((current) => current ? {
        ...current,
        designChanges: current.designChanges.map((change) => (
          change.id === designChangeId ? { ...change, ...updated } : change
        )),
      } : current);
    });
  };

  const handleMatrixImport = async (file: File) => {
    if (!sessionId) return;
    await runSafely(async () => {
      const matrix = await drbfmService.importFunctionPartMatrix(sessionId, file);
      setWorkflow((current) => current ? {
        ...current,
        functionPartMatrix: matrix,
      } : current);
    });
  };

  const handleMatrixPrefill = async () => {
    if (!sessionId) return;
    await runSafely(async () => {
      const matrix = await drbfmService.prefillFunctionPartMatrix(sessionId, {
        item_name: itemName,
      });
      setWorkflow((current) => current ? {
        ...current,
        functionPartMatrix: matrix,
      } : current);
    });
  };

  const handleMatrixCellUpdate = async (
    cellId: string,
    patch: MatrixCellUpdateRequest,
  ) => {
    if (!sessionId) return;
    await runSafely(async () => {
      const matrix = await drbfmService.updateFunctionPartMatrixCell(
        sessionId,
        cellId,
        patch,
      );
      setWorkflow((current) => current ? {
        ...current,
        functionPartMatrix: matrix,
      } : current);
    });
  };

  const handleMatrixPromotion = async () => {
    if (!sessionId) return;
    await runSafely(async () => {
      const result = await drbfmService.promoteFunctionPartMatrix(sessionId);
      setWorkflow(result.workflow);
    });
  };

  const handleGenerateFailureModes = async () => {
    if (!sessionId) return;
    if (!(await confirmRegenerate(workflow?.failureModes.length ?? 0))) return;
    await runSafely(async () => {
      const failureModes = await drbfmService.runWorkflowStage(
        sessionId,
        'generate_failure_modes',
      );
      setWorkflow(failureModes.workflow);
      if (failureModes.stageRun.status !== 'completed') {
        setError(t('drbfm.workflow.logic.generateFailureModesFailed', '故障モード生成に失敗しました'));
      }
    });
  };

  const handleReviewFailureMode = async (
    failureModeId: string,
    request: UpdateFailureModeReviewRequest,
  ) => {
    if (!sessionId) return;
    await runSafely(async () => {
      const updated = await drbfmService.updateFailureModeReview(
        sessionId,
        failureModeId,
        request,
      );
      const readiness = await drbfmService.getReadiness(sessionId);
      setWorkflow((current) => current ? {
        ...current,
        failureModes: current.failureModes.map((mode) => (
          mode.id === failureModeId ? updated : mode
        )),
        readiness,
      } : current);
    });
  };

  const handleGenerateCountermeasures = async () => {
    if (!sessionId) return;
    if (!(await confirmRegenerate(workflow?.countermeasures.length ?? 0))) return;
    await runSafely(async () => {
      const result = await drbfmService.runWorkflowStage(
        sessionId,
        'generate_countermeasures',
      );
      setWorkflow(result.workflow);
      if (result.stageRun.status !== 'completed') {
        setError(t('drbfm.workflow.logic.generateCountermeasuresFailed', '対策案生成に失敗しました'));
      }
    });
  };

  const handleReviewCountermeasure = async (
    countermeasureId: string,
    request: UpdateCountermeasureReviewRequest,
  ) => {
    if (!sessionId) return;
    await runSafely(async () => {
      const updated = await drbfmService.updateCountermeasureReview(
        sessionId,
        countermeasureId,
        request,
      );
      const readiness = await drbfmService.getReadiness(sessionId);
      setWorkflow((current) => current ? {
        ...current,
        countermeasures: current.countermeasures.map((measure) => (
          measure.id === countermeasureId ? updated : measure
        )),
        readiness,
      } : current);
    });
  };

  const handleExport = async (mode: 'draft' | 'reviewed' | 'scored') => {
    if (!sessionId) return;
    await runSafely(async () => {
      await drbfmService.downloadExportedExcel(
        sessionId,
        `drbfm_${sessionId}_${mode}.xlsx`,
        mode,
      );
    });
  };

  const reviewedCount = countReviewedDesignChanges(workflow);
  const reviewedFailureModeCount = countReviewedFailureModes(workflow);

  return {
    startValue,
    setStartValue,
    itemName,
    setItemName,
    workflow,
    sessionId,
    oldText,
    setOldText,
    newText,
    setNewText,
    changeReportText,
    setChangeReportText,
    oldCadModelId,
    setOldCadModelId,
    newCadModelId,
    setNewCadModelId,
    isLoading,
    error,
    reviewedCount,
    reviewedFailureModeCount,
    handleStart,
    handleExtractChanges,
    handleReviewChange,
    handleConfirmComparability,
    handleMatrixImport,
    handleMatrixPrefill,
    handleMatrixCellUpdate,
    handleMatrixPromotion,
    handleGenerateFailureModes,
    handleReviewFailureMode,
    handleGenerateCountermeasures,
    handleReviewCountermeasure,
    handleExport,
  };
};
